import { Link } from 'react-router-dom';
import { ArrowRight, PhoneCall, Waves, CalendarCheck, PhoneForwarded, TrendingUp } from 'lucide-react';
import { useSEO } from '../lib/seo';
import { Reveal, SectionHeading } from '../components/Reveal';
import { Waveform } from '../components/mockui';

const CAPABILITIES = [
  { icon: <PhoneCall className="h-5 w-5" />, title: 'Automated call handling', copy: 'Every inbound call answered instantly, day and night.' },
  { icon: <Waves className="h-5 w-5" />, title: 'Natural human-like conversations', copy: "Callers hear a warm, human voice — they won't realize it's AI." },
  { icon: <CalendarCheck className="h-5 w-5" />, title: 'Appointment booking and call routing', copy: 'Meetings booked on the call and callers routed to the right place.' },
  { icon: <PhoneForwarded className="h-5 w-5" />, title: 'Works with business phone systems', copy: 'Plugs into the phone setup you already use.' },
  { icon: <TrendingUp className="h-5 w-5" />, title: 'Scalable for high call volumes', copy: 'One call or one hundred at once — CapableBee keeps up.' },
];

export const Products = () => {
  useSEO({
    title: 'Products — AI Voice Agent | CapableBee',
    description:
      'Handle phone calls automatically with intelligent voice AI. Automated call handling, natural conversations, appointment booking and call routing.',
  });

  return (
    <main className="pt-[72px]" data-testid="products-page">
      <section className="relative overflow-hidden bg-gradient-to-b from-brand-ice via-brand-iceSoft to-white py-16 lg:py-24">
        <div className="bg-grid mask-fade-b pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="container-x relative grid items-center gap-12 lg:grid-cols-2">
          <div>
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-lineBlue bg-white/80 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-brand-skyDeep shadow-card">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-honey" aria-hidden="true" />
                Products
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-brand-navy sm:text-5xl lg:text-6xl">
                AI Voice <span className="bg-gradient-to-r from-brand-sky to-brand-skyDeep bg-clip-text text-transparent">Agent</span>
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-brand-body/90 sm:text-lg">
                Handle phone calls automatically with intelligent voice AI — so your business never misses a call,
                a lead, or an appointment again.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="products-cta"
                  className="group inline-flex items-center gap-2 rounded-full bg-brand-sky px-7 py-3.5 text-sm font-bold text-white shadow-soft transition-all hover:-translate-y-0.5 hover:bg-brand-skyDeep hover:shadow-lift"
                >
                  Get Started
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="relative mx-auto w-full max-w-[560px] overflow-hidden rounded-3xl border border-brand-lineBlue/70 bg-white shadow-lift">
              <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
                <span className="h-3 w-3 rounded-full bg-red-400" aria-hidden="true" />
                <span className="h-3 w-3 rounded-full bg-amber-400" aria-hidden="true" />
                <span className="h-3 w-3 rounded-full bg-emerald-400" aria-hidden="true" />
                <span className="ml-3 text-xs font-semibold text-slate-400">CapableBee Dashboard</span>
              </div>
              <img
                src="/brand/dash.png"
                alt="CapableBee Call History dashboard showing inbound calls with duration, status, cost, recordings and summaries"
                width="1346"
                height="708"
                loading="lazy"
                className="w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="container-x">
          <SectionHeading
            eyebrow="What It Does"
            title="One agent for every conversation"
            sub="CapableBee's AI Voice Agent handles phone calls automatically with intelligent voice AI."
          />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.06} data-testid={`product-capability-${i}`}>
                <div className="h-full rounded-3xl border border-brand-line bg-white p-7 shadow-card transition-all duration-300 hover:-translate-y-1 hover:shadow-lift">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-ice text-brand-skyDeep">
                    {c.icon}
                  </span>
                  <h3 className="mt-5 text-lg font-bold tracking-tight text-brand-navy">{c.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{c.copy}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="flex h-full flex-col justify-between rounded-3xl bg-gradient-to-b from-brand-navy to-brand-panel p-7 text-white shadow-lift">
                <Waveform bars={14} className="h-10" tone="bg-brand-honey/80" />
                <div>
                  <p className="text-lg font-bold">Hear the difference</p>
                  <p className="mt-2 text-sm text-white/70">
                    Book a demo today and see how CapableBee can turn conversations into leads.
                  </p>
                  <Link
                    to="/contact"
                    data-testid="product-demo-cta"
                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-extrabold text-brand-navy transition-all hover:-translate-y-0.5"
                  >
                    Book a Demo
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
};
